export enum ERole {
  SuperAdmin = 'SuperAdmin',
  SupportAdmin = 'SupportAdmin',
  /**
   * Admin of a B2B group. "Organization admin" is NOT a separate role — a GroupAdmin whose group is
   * an Organization is treated as org-scoped (see UserProfile.isOrganizationScope / organizationId).
   * Org-ness is derived from the group type on the server, never a distinct role.
   */
  GroupAdmin = 'GroupAdmin',
  /** Admin of a single Sub-Group within an Organization. Sub-group-scoped, not elevated. */
  SubGroupAdmin = 'SubGroupAdmin',
  Learner = 'Learner',
}

/** True for roles that bypass group scoping (see backend Roles.IsElevated). */
export function isElevatedRole(role: ERole | null): boolean {
  return role === ERole.SuperAdmin || role === ERole.SupportAdmin;
}

export type AnyRole = ERole | 'admin' | 'user';

export function normalizeRole(r?: AnyRole | string | null | undefined): ERole | null {
  if (!r) return null;
  const v = String(r);

  if (v === 'user') return ERole.Learner;
  if (v === 'admin') return ERole.SuperAdmin;

  if ((Object.values(ERole) as string[]).includes(v)) {
    return v as ERole;
  }
  return null;
}

/**
 * UI label for a role. A GroupAdmin of an Organization is shown as "Organization Admin" — a display
 * name only; the stored role stays GroupAdmin. Unknown values are returned unchanged.
 */
export function getRoleDisplayLabel(role?: string | null, opts?: { isOrganization?: boolean }): string {
  switch (normalizeRole(role ?? null)) {
    case ERole.SuperAdmin:
      return 'Super Admin';
    case ERole.SupportAdmin:
      return 'Support Admin';
    case ERole.GroupAdmin:
      return opts?.isOrganization ? 'Organization Admin' : 'Group Admin';
    case ERole.SubGroupAdmin:
      return 'Sub-Group Admin';
    case ERole.Learner:
      return 'Learner';
    default:
      return role ?? '';
  }
}

/** Filter label for the GroupAdmin role when the list mixes standalone groups and Organizations. */
export const GROUP_OR_ORGANIZATION_ADMIN_LABEL = 'Group / Organization Admin';

/** GroupAdmin whose group is an Organization (derived from /me, not a separate role). */
export function isOrgScopedGroupAdmin(
  profile?: { role?: string; isOrganizationScope?: boolean; organizationId?: string | null } | null,
): boolean {
  return (
    normalizeRole(profile?.role ?? null) === ERole.GroupAdmin &&
    !!(profile?.isOrganizationScope || profile?.organizationId)
  );
}
